/**
 * Balance API utilities with caching
 */

import { getBalanceData, clearCache } from './apiClient';

// Cache balance in localStorage for instant loading
const getCachedBalance = () => {
  if (typeof localStorage !== 'undefined') {
    try {
      const cached = localStorage.getItem('user_balance');
      if (cached) {
        const parsed = JSON.parse(cached);
        // Check if cache is still valid (5 minutes)
        if (parsed.timestamp && Date.now() - parsed.timestamp < 5 * 60 * 1000) {
          return parsed.data;
        }
      }
    } catch (e) {
      // Ignore cache errors
    }
  }
  return null;
};

const cacheBalance = (data) => {
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem('user_balance', JSON.stringify({
        data,
        timestamp: Date.now(),
      }));
    } catch (e) {
      // Ignore cache errors
    }
  }
};

/**
 * Get user balance with caching
 */
export const getBalance = async (useCache = true) => {
  try {
    // Return cached balance immediately if available
    if (useCache) {
      const cached = getCachedBalance();
      if (cached) {
        // Refresh in background
        getBalance(false).then(result => {
          if (result && !result.error) {
            cacheBalance(result);
          }
        }).catch(() => {
          // Ignore background refresh errors
        });
        return cached;
      }
    }

    // Fetch from API with longer timeout
    const result = await getBalanceData(useCache);
    
    // Cache the result
    if (result && !result.error) {
      cacheBalance(result);
    }

    return result;
  } catch (error) {
    // Return cached balance if available, even if stale
    const cached = getCachedBalance();
    if (cached) {
      // Silently use cached data - timeout errors are expected when server is slow
      // Only log if it's not a timeout (which is handled gracefully)
      if (!error.message || !error.message.includes('timeout')) {
        console.warn('Using cached balance due to API error:', error.message);
      }
      return cached;
    }
    // Only log error if we don't have cached data to fall back to
    console.error('Error getting balance (no cache available):', error);
    throw error;
  }
};

/**
 * Clear balance cache (call after balance changes)
 */
export const clearBalanceCache = () => {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('user_balance');
  }
  clearCache('GET:/api/balance:');
};

/**
 * Get auth headers for API requests
 */
const getAuthHeaders = async () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

/**
 * Create a withdrawal request
 */
export const createWithdrawal = async (
  amount,
  paymentMethod = 'stripe',
  currency = 'USD',
  paypalEmail = null,
  cryptoWalletAddress = null,
  cryptoCurrency = 'BTC',
  coingateBeneficiary = null
) => {
  try {
    const headers = await getAuthHeaders();
    const body = { 
      amount, 
      payment_method: paymentMethod,
      currency: currency // Send user's selected currency
    };

    // Add PayPal email if PayPal is selected
    if (paymentMethod === 'paypal' && paypalEmail) {
      body.paypal_email = paypalEmail;
    }

    // Add CoinGate wallet address and currency if CoinGate is selected
    if (paymentMethod === 'coingate' && cryptoWalletAddress) {
      body.crypto_wallet_address = cryptoWalletAddress;
      body.crypto_currency = cryptoCurrency;
      if (coingateBeneficiary) {
        body.coingate_beneficiary = coingateBeneficiary;
      }
    }

    const response = await fetch('/api/balance/withdraw', {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    const result = await response.json();
    if (!response.ok) {
      // Check for specific error codes
      if (result.code === 'BALANCE_INSUFFICIENT') {
        throw new Error(
          result.error + '\n\n' + (result.details || 'The platform needs funds to process withdrawals.')
        );
      }
      throw new Error(result.error || result.details || 'Failed to create withdrawal');
    }

    return result;
  } catch (error) {
    console.error('Error creating withdrawal:', error);
    throw error;
  }
};

